/**
 * Flagged Breaks Page (PR 1, Phase B1)
 *
 * Paginated list of all flagged breaks across the team with an
 * Acknowledge action per row. Acknowledged rows remain visible but
 * are visually de-emphasised so reviewers can focus on the
 * outstanding queue.
 *
 * Route: /admin/hr/breaks/flagged
 * RBAC:  admin / team_leader / tax_team_leader / title_team_leader / manager
 */

import React, { useState, useEffect, useCallback } from 'react';
import { isHrReadOnly } from '../../lib/hrRole';
import axios from 'axios';
import { CheckCircle2, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { AcknowledgeBreakModal } from '../../components/hr/break/AcknowledgeBreakModal';
import { BreakEmptyState, BreakPageHeader, BreakTableCard, BreakLoadingState, BreakErrorState, BreakPagination, BreakStatusPill, BreakAvatar } from '../../components/hr/break/ui/BreakUi';
import {
  BreakStatusBadge,
  BreakDurationCell,
  formatDateTimeShortET,
} from '../../components/hr/break/breakUiHelpers';

const API = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_SIZE = 25;

const FlaggedBreaksPage = () => {
  const hrReadOnly = isHrReadOnly();
  const [breaks, setBreaks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [ackTarget, setAckTarget] = useState(null);
  const [error, setError] = useState(null);

  const fetchFlagged = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(
        `${API}/api/breaks/flagged?page=${page}&limit=${PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBreaks(resp.data.breaks || []);
      setTotal(resp.data.total || 0);
      setError(null);
    } catch (err) {
      console.error('Failed to load flagged breaks:', err);
      setError(err?.response?.data?.detail || err.message || 'Failed to load flagged breaks');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchFlagged(); }, [fetchFlagged]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const outstandingCount = breaks.filter((b) => !b.admin_acknowledged).length;

  return (
    <>
      <BreakPageHeader title="Flagged Breaks Review" subtitle="Breaks that exceeded their type cap, the daily cap, or were auto/force-ended. Acknowledge to mark as reviewed." testId="flagged-header"
        action={!loading && !error && <BreakStatusPill tone={outstandingCount ? 'red' : 'green'} dot testId="flagged-outstanding-count">{outstandingCount} outstanding on this page</BreakStatusPill>} />

      {loading ? (
        <BreakTableCard><BreakLoadingState label="Loading flagged breaks…" testId="flagged-loading" /></BreakTableCard>
      ) : error ? (
        <BreakErrorState title="Couldn't load flagged breaks." detail={error} onRetry={fetchFlagged} testId="flagged-error" />
      ) : breaks.length === 0 ? (
        <BreakEmptyState title="No flagged breaks" subtitle="No break exceptions require review. Breaks that exceed type or daily caps will appear here for triage." testId="flagged-empty" />
      ) : (
        <>
          <BreakTableCard testId="flagged-table-wrapper">
            <Table className="tf-break-table">
              <TableHeader>
                <TableRow className="bm-thead">
                  <TableHead>Employee</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Flag Reason</TableHead>
                  <TableHead>Reviewed?</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {breaks.map((b) => (
                  <TableRow
                    key={b.id}
                    data-testid={`flagged-row-${b.id}`}
                    className={`bm-row ${b.admin_acknowledged ? 'opacity-60' : ''}`}
                  >
                    <TableCell className="font-medium"><span className="flex items-center gap-2.5"><BreakAvatar name={b.user_name || b.user_id} />{b.user_name || b.user_id}</span></TableCell>
                    <TableCell className="text-xs text-gray-700">{formatDateTimeShortET(b.start_time)}</TableCell>
                    <TableCell>{b.break_type_name}</TableCell>
                    <TableCell className="text-right">
                      <BreakDurationCell minutes={b.duration_minutes} maxMinutes={b.max_duration_minutes} />
                    </TableCell>
                    <TableCell><BreakStatusBadge status={b.status} flagged={b.flagged} /></TableCell>
                    <TableCell className="text-xs text-gray-700 max-w-xs truncate" title={b.flagged_reason || ''}>
                      {b.flagged_reason || '—'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {b.admin_acknowledged ? (
                        <div className="flex items-center gap-1 text-emerald-700" title={b.admin_note ? `Note: ${b.admin_note}` : undefined} data-testid={`ack-reviewed-${b.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>{b.admin_acknowledged_by_name || 'Reviewed'}</span>
                        </div>
                      ) : (
                        <span className="text-amber-700">Outstanding</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {hrReadOnly && !b.admin_acknowledged ? (
                        <span className="text-xs text-slate-400" data-testid={`ack-readonly-${b.id}`}>View only</span>
                      ) : (
                      <Button
                        variant={b.admin_acknowledged ? 'ghost' : 'outline'}
                        size="sm"
                        onClick={() => setAckTarget(b)}
                        className={`bm-btn h-8 rounded-lg ${b.admin_acknowledged ? 'text-slate-600' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                        data-testid={`ack-btn-${b.id}`}
                      >
                        {b.admin_acknowledged
                          ? <><FileText className="w-3.5 h-3.5 mr-1" /> View note</>
                          : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Acknowledge</>}
                      </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </BreakTableCard>

          <BreakPagination page={page} pages={pages} shown={breaks.length} total={total} noun={`flagged break${total === 1 ? '' : 's'}`}
            onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(pages, p + 1))} testId="flagged-pagination" />
        </>
      )}

      <AcknowledgeBreakModal
        open={!!ackTarget}
        onOpenChange={(v) => !v && setAckTarget(null)}
        breakRecord={ackTarget}
        onSuccess={() => { setAckTarget(null); fetchFlagged(); }}
      />
    </>
  );
};

export default FlaggedBreaksPage;
